import { Vector3 } from '../../types/Vector3'
import { Quaternion } from '../../types/Quaternion'
import { LayoutInterface, AlignmentMode } from '../FieldTypes'
import { FieldParameters } from '../FieldParameters'
import { CurveUtils } from '../../../math/CurveUtils'
import { VectorUtils } from '../../../math/VectorUtils'

export class LineLayout implements LayoutInterface {
  // Static logging throttle to prevent spam
  private static lastLogTime: number = 0
  
  // ==================== POSITION CALCULATION ====================
  
  /**
   * Calculate position for object at given index
   */
  public computePosition(index: number, totalCount: number, params: FieldParameters): Vector3 {
    if (totalCount <= 1) {
      return new Vector3()
    }
    
    // Calculate parameter t (0 to 1)
    const t = index / (totalCount - 1)
    
    // Calculate spacing
    const spacing = this.calculateSpacing(params, totalCount)
    
    // Get line direction from parameters
    const lineDirection = this.getLineDirection(params)
    
    // Calculate total line length
    const totalLength = spacing * (totalCount - 1)
    
    // Calculate line endpoints along fieldDirection
    const startPoint = lineDirection.clone().multiplyScalar(-totalLength / 2)
    const endPoint = lineDirection.clone().multiplyScalar(totalLength / 2)
    
    // Get point on line
    const position = CurveUtils.getPointOnLine(startPoint, endPoint, t)
    
    return position
  }
  
  // ==================== ORIENTATION CALCULATION ====================
  
  /**
   * Calculate orientation for object at given index using improved Frenet frame math
   */
  public computeOrientation(index: number, totalCount: number, params: FieldParameters): Quaternion {
    const t = index / (totalCount - 1)
    
    // Get line endpoints
    const lineDirection = this.getLineDirection(params).normalize()
    const spacing = this.calculateSpacing(params, totalCount)
    const totalLength = spacing * (totalCount - 1)
    const startPoint = lineDirection.clone().multiplyScalar(-totalLength / 2)
    const endPoint = lineDirection.clone().multiplyScalar(totalLength / 2)
    
    // Get Frenet frame for the line
    const frenetFrame = CurveUtils.getLineFrenetFrame(startPoint, endPoint, t)
    
    // Determine up mode based on parameters
    const upMode = params.upVectorMode === 'world-up' ? 'world' : 'path'
    
    // Determine alignment mode
    const orientation = params.alignmentMode === AlignmentMode.Aligned ? 'aligned' : 'perpendicular'
    
    // Calculate orientation using the improved math
    const { right, up, forward } = CurveUtils.calculateOrientation(
      frenetFrame.tangent,
      frenetFrame.normal,
      frenetFrame.binormal,
      orientation,
      upMode
    )
    
    // Throttled logging to prevent spam
    const now = Date.now()
    if (now - LineLayout.lastLogTime > 5000 && index === 0) { // Log only once every 5 seconds for first object
      //console.log(`🔄 Line ${orientation.toUpperCase()} ${upMode.toUpperCase()}-UP:`)
      //console.log(`  Tangent: [${frenetFrame.tangent.x.toFixed(2)}, ${frenetFrame.tangent.y.toFixed(2)}, ${frenetFrame.tangent.z.toFixed(2)}]`)
      //console.log(`  Normal:  [${frenetFrame.normal.x.toFixed(2)}, ${frenetFrame.normal.y.toFixed(2)}, ${frenetFrame.normal.z.toFixed(2)}]`)
      //console.log(`  Right:   [${right.x.toFixed(2)}, ${right.y.toFixed(2)}, ${right.z.toFixed(2)}]`)
      //console.log(`  Up:      [${up.x.toFixed(2)}, ${up.y.toFixed(2)}, ${up.z.toFixed(2)}]`)
      //console.log(`  Forward: [${forward.x.toFixed(2)}, ${forward.y.toFixed(2)}, ${forward.z.toFixed(2)}]`)
      LineLayout.lastLogTime = now
    }
    
    // Create rotation matrix from the basis vectors
    return VectorUtils.lookRotation(forward, up)
  }
  
  // ==================== TANGENT CALCULATION ====================
  
  /**
   * Calculate tangent vector (direction along line)
   */
  public computeTangent(index: number, totalCount: number, params: FieldParameters): Vector3 {
    return this.getLineDirection(params)
  }
  
  // ==================== NORMAL CALCULATION ====================
  
  /**
   * Calculate normal vector (perpendicular to line)
   */
  public computeNormal(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const tangent = this.computeTangent(index, totalCount, params)
    return VectorUtils.getNormal(tangent, params.customUpVector)
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Get line direction vector
   */
  private getLineDirection(params: FieldParameters): Vector3 {
    // Use the main field direction for line layouts
    return params.fieldDirection.clone().normalize()
  }
  
  /**
   * Get main directional vector of the layout (line direction)
   */
  public getDirectionalVector(params: FieldParameters): Vector3 {
    return params.fieldDirection.clone().normalize()
  }
  
  /**
   * Calculate spacing between objects
   */
  private calculateSpacing(params: FieldParameters, totalCount: number): number {
    if (params.useObjectSpacing && params.objectBounds) {
      // Use object bounds for spacing
      const objectSize = params.objectBounds.size
      const maxSize = Math.max(objectSize.x, objectSize.y, objectSize.z)
      return maxSize * params.spacingMultiplier
    } else {
      // Use fixed cell size plus padding
      return params.cellSize + params.cellSpacing
    }
  }
  
  // ==================== ADVANCED FEATURES ====================
  
  /**
   * Get line length
   */
  public getLineLength(totalCount: number, params: FieldParameters): number {
    if (totalCount <= 1) return 0
    
    const spacing = this.calculateSpacing(params, totalCount)
    return spacing * (totalCount - 1)
  }
  
  /**
   * Get point at specific distance along line
   */
  public getPointAtDistance(distance: number, totalCount: number, params: FieldParameters): Vector3 {
    const lineLength = this.getLineLength(totalCount, params)
    const t = Math.max(0, Math.min(1, distance / lineLength))
    
    // Get line direction from parameters
    const lineDirection = this.getLineDirection(params)
    const totalLength = lineLength
    
    // Calculate line endpoints along fieldDirection
    const startPoint = lineDirection.clone().multiplyScalar(-totalLength / 2)
    const endPoint = lineDirection.clone().multiplyScalar(totalLength / 2)
    
    return CurveUtils.getPointOnLine(startPoint, endPoint, t)
  }
  
  /**
   * Find closest point on line
   */
  public findClosestPoint(targetPoint: Vector3, totalCount: number, params: FieldParameters): {
    point: Vector3
    index: number
    distance: number
  } {
    // Get line direction from parameters
    const lineDirection = this.getLineDirection(params)
    const spacing = this.calculateSpacing(params, totalCount)
    const totalLength = spacing * (totalCount - 1)
    
    // Calculate line endpoints along fieldDirection
    const startPoint = lineDirection.clone().multiplyScalar(-totalLength / 2)
    const endPoint = lineDirection.clone().multiplyScalar(totalLength / 2)
    
    // Project point onto line
    const lineVector = new Vector3(
      endPoint.x - startPoint.x,
      endPoint.y - startPoint.y,
      endPoint.z - startPoint.z
    )
    const pointVector = new Vector3(
      targetPoint.x - startPoint.x,
      targetPoint.y - startPoint.y,
      targetPoint.z - startPoint.z
    )
    const projection = pointVector.dot(lineVector) / lineVector.lengthSquared()
    const t = Math.max(0, Math.min(1, projection))
    
    const closestPoint = CurveUtils.getPointOnLine(startPoint, endPoint, t)
    const closestIndex = Math.round(t * (totalCount - 1))
    const distance = targetPoint.distanceTo(closestPoint)
    
    return {
      point: closestPoint,
      index: closestIndex,
      distance
    }
  }
} 